import { jsx as _jsx } from "react/jsx-runtime";
export const mentionSuggestionsLimit = 8;
export const MENTION_TOKEN_REGEX = /\[([^\]]+)\]\(mention:(admin|contact|client|helloDarwin)(?::([^)]+))?\)/g;
export const INLINE_MENTION_SCAN_REGEX = /(^|\s)@([^\s@]+)/g;
export const MENTION_TRAILING_PUNCTUATION_REGEX = /[^\w-]+$/;
export const MENTION_TRIGGER_REGEX = /(^|\s)@([^\s@]*)$/;
export const renderCanonicalMentions = (content, purple, orange) => {
    var _a;
    const parts = [];
    let lastIndex = 0;
    MENTION_TOKEN_REGEX.lastIndex = 0;
    let m;
    while ((m = MENTION_TOKEN_REGEX.exec(content)) !== null) {
        const idx = (_a = m.index) !== null && _a !== void 0 ? _a : 0;
        if (idx > lastIndex)
            parts.push(content.slice(lastIndex, idx));
        const label = m[1];
        const t = m[2];
        const color = t === 'admin' || t === 'helloDarwin' ? purple : orange;
        parts.push(_jsx("span", { style: { color }, children: label }, `${idx}-${label}`));
        lastIndex = idx + m[0].length;
    }
    if (lastIndex < content.length)
        parts.push(content.slice(lastIndex));
    return parts.length > 0 ? parts : content;
};
export const buildMentionSuggestions = (mentionCandidates = [], query, limit = mentionSuggestionsLimit) => {
    var _a;
    const uniq = new Map();
    for (const c of mentionCandidates) {
        const key = `${c.targetType}:${(_a = c.targetId) !== null && _a !== void 0 ? _a : ''}:${c.label}`;
        if (!uniq.has(key))
            uniq.set(key, c);
    }
    const all = Array.from(uniq.values());
    const q = (query !== null && query !== void 0 ? query : '').trim().toLowerCase();
    const list = q ? all.filter((c) => c.label.toLowerCase().includes(q)) : all;
    return list.slice(0, limit);
};
export const formatMentionCandidatesInline = (candidates, conjunction = 'et') => {
    const tokens = candidates.map((c) => `@${c.label}`);
    if (tokens.length <= 2) {
        return tokens.join(` ${conjunction} `);
    }
    return `${tokens.slice(0, -1).join(', ')} ${conjunction} ${tokens[tokens.length - 1]}`;
};
export const buildCanonicalContent = (input, mentionInstances, mentionCandidates = []) => {
    var _a;
    const trimmed = input.trim();
    if (!trimmed)
        return trimmed;
    let out = '';
    let i = 0;
    const used = new Set();
    const positions = [];
    const occurrences = (label) => {
        const needle = `@${label}`;
        const pos = [];
        let idx = 0;
        while (idx <= trimmed.length) {
            const found = trimmed.indexOf(needle, idx);
            if (found === -1)
                break;
            pos.push(found);
            idx = found + needle.length;
        }
        return pos;
    };
    const labelPositionsMap = new Map();
    for (const inst of mentionInstances) {
        if (!labelPositionsMap.has(inst.label)) {
            labelPositionsMap.set(inst.label, occurrences(inst.label));
        }
        const posArr = labelPositionsMap.get(inst.label);
        let chosenIndex = -1;
        for (let k = 0; k < posArr.length; k++) {
            if (!used.has(posArr[k])) {
                chosenIndex = posArr[k];
                used.add(posArr[k]);
                break;
            }
        }
        if (chosenIndex !== -1) {
            positions.push({
                start: chosenIndex,
                end: chosenIndex + `@${inst.label}`.length,
                inst,
            });
        }
    }
    positions.sort((a, b) => a.start - b.start);
    for (const seg of positions) {
        if (seg.start > i)
            out += trimmed.slice(i, seg.start);
        const inst = seg.inst;
        const token = inst.targetType === 'admin' || inst.targetType === 'contact'
            ? `[@${inst.label}](mention:${inst.targetType}:${inst.targetId})`
            : `[@${inst.label}](mention:${inst.targetType})`;
        out += token;
        i = seg.end;
    }
    if (i < trimmed.length)
        out += trimmed.slice(i);
    const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (mentionCandidates && mentionCandidates.length > 0) {
        for (const c of mentionCandidates) {
            const label = String((_a = c.label) !== null && _a !== void 0 ? _a : '').trim();
            if (!label)
                continue;
            const token = '@' + label;
            const idPart = c.targetType === 'admin' || c.targetType === 'contact'
                ? `:${c.targetId}`
                : '';
            const replacement = `[@${label}](mention:${c.targetType}${idPart})`;
            const re = new RegExp(`(^|\\s)${escapeRegExp(token)}(?![\\w-])`, 'g');
            out = out.replace(re, (m, p1) => `${p1}${replacement}`);
        }
    }
    return out;
};
export const hasValidMention = (content, mentionInstances) => {
    var _a;
    MENTION_TOKEN_REGEX.lastIndex = 0;
    const hasCanonical = MENTION_TOKEN_REGEX.test(content);
    if (hasCanonical)
        return true;
    if (((_a = mentionInstances === null || mentionInstances === void 0 ? void 0 : mentionInstances.length) !== null && _a !== void 0 ? _a : 0) > 0)
        return true;
    return false;
};
export const pruneMentionInstances = (text, instances) => {
    var _a;
    if (!instances || instances.length === 0)
        return instances;
    const counts = new Map();
    const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const uniqueLabels = Array.from(new Set(instances
        .map((inst) => { var _a; return String((_a = inst.label) !== null && _a !== void 0 ? _a : '').trim(); })
        .filter((s) => !!s)));
    for (const label of uniqueLabels) {
        const token = '@' + label;
        const re = new RegExp(`(^|\\s)${escapeRegExp(token)}(?![\\w-])`, 'gi');
        let n = 0;
        while (re.exec(text) !== null) {
            n++;
        }
        counts.set(label.toLowerCase(), n);
    }
    const total = Array.from(counts.values()).reduce((a, b) => a + b, 0);
    if (total === 0)
        return [];
    const next = [];
    for (const inst of instances) {
        const key = String(inst.label).toLowerCase();
        const n = (_a = counts.get(key)) !== null && _a !== void 0 ? _a : 0;
        if (n > 0) {
            next.push(inst);
            counts.set(key, n - 1);
        }
    }
    return next;
};
